import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { request } from "../utils/api";
import { getRoles } from "../Auth";
import {
  SearchIcon,
  ChevronDownIcon,
  EditIcon,
  MapPinIcon,
  ClockIcon,
} from "../Icons";
import toast from "react-hot-toast";
import Table from "../components/UI/Table";
import CoverageAreaModal from "../components/Profile/CoverageAreaModal";
import AvailabilityModal from "../components/Profile/AvailabilityModal";
import useCurrentUser from "../hooks/useCurrentUser";
import {
  canEditOthersFromCoverageList,
  canManageRowCoverageActions,
} from "../utils/coverageAreaPermissions";
import {
  PAGE_SHELL,
  PAGE_HEADER_ROW,
  PAGE_SUBTITLE,
  CELL_MUTED,
  CELL_STRONG,
} from "../ui/pageChrome";
import {
  COVERAGE_THEME,
  COVERAGE_BTN,
  COVERAGE_FIELD,
  COVERAGE_CHECKBOX,
  COVERAGE_ROLE_CHIP,
  COVERAGE_COL,
} from "./coverageAreaTheme";
import {
  CUSTOM_DAY_OPTIONS,
  DAY_PRESET_OPTIONS,
  SHIFT_DURATION_OPTIONS,
  SHIFT_TYPE_OPTIONS,
  availabilityStatusDotClass,
} from "../utils/userAvailability";

const DEFAULT_LIMIT = 20;

const TRI_STATE_OPTIONS = [
  { value: "all", label: "All Users" },
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

const countSelections = (...sets) =>
  sets.reduce((sum, set) => sum + (set?.size || 0), 0);

const countFilterState = ({
  search,
  cityIds,
  roleCodes,
  dayPresets,
  customDays,
  shiftDurations,
  shiftTypes,
  hasAvailability,
  hasCoverage,
}) =>
  countSelections(
    cityIds,
    roleCodes,
    dayPresets,
    customDays,
    shiftDurations,
    shiftTypes,
  ) +
  (search?.trim() ? 1 : 0) +
  (hasAvailability !== "all" ? 1 : 0) +
  (hasCoverage !== "all" ? 1 : 0);

const CoverageArea = () => {
  const { user: sessionUser } = useCurrentUser();
  const canEditListActions = canEditOthersFromCoverageList(sessionUser);

  const [users, setUsers] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(DEFAULT_LIMIT);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState("");
  const [selectedCityIds, setSelectedCityIds] = useState(new Set());
  const [selectedRoleCodes, setSelectedRoleCodes] = useState(new Set());
  const [selectedDayPresets, setSelectedDayPresets] = useState(new Set());
  const [selectedCustomDays, setSelectedCustomDays] = useState(new Set());
  const [selectedShiftDurations, setSelectedShiftDurations] = useState(new Set());
  const [selectedShiftTypes, setSelectedShiftTypes] = useState(new Set());
  const [hasAvailability, setHasAvailability] = useState("all");
  const [hasCoverage, setHasCoverage] = useState("all");
  const [citySearch, setCitySearch] = useState("");
  const [roleSearch, setRoleSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [appliedCityIds, setAppliedCityIds] = useState(new Set());
  const [appliedRoleCodes, setAppliedRoleCodes] = useState(new Set());
  const [appliedDayPresets, setAppliedDayPresets] = useState(new Set());
  const [appliedCustomDays, setAppliedCustomDays] = useState(new Set());
  const [appliedShiftDurations, setAppliedShiftDurations] = useState(new Set());
  const [appliedShiftTypes, setAppliedShiftTypes] = useState(new Set());
  const [appliedHasAvailability, setAppliedHasAvailability] = useState("all");
  const [appliedHasCoverage, setAppliedHasCoverage] = useState("all");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [coverageModalUser, setCoverageModalUser] = useState(null);
  const [availabilityModalUser, setAvailabilityModalUser] = useState(null);

  const closeActionMenus = useCallback(() => {
    window.dispatchEvent(new CustomEvent("coverage-area-close-menus"));
  }, []);

  const columns = useMemo(() => {
    const base = [
      {
        header: "Name",
        accessor: "firstName",
        className: COVERAGE_COL.name,
        render: (u) => (
          <span className={`${CELL_STRONG} text-slate-900`}>
            {[u.firstName, u.lastName].filter(Boolean).join(" ") || "—"}
          </span>
        ),
      },
      {
        header: "Email",
        accessor: "email",
        className: COVERAGE_COL.email,
        render: (u) => (
          <span className={`block truncate ${CELL_MUTED}`} title={u.email || ""}>
            {u.email || "—"}
          </span>
        ),
      },
      {
        header: "Role",
        accessor: "role",
        className: COVERAGE_COL.role,
        render: (u) => <RoleBadge name={u.role?.name} />,
      },
      {
        header: "Coverage Areas",
        accessor: "coverageAreas",
        className: COVERAGE_COL.areas,
        render: (u) =>
          u.coverageAreas?.length > 0 ? (
            <span
              className="text-[13px] text-slate-700 line-clamp-2"
              title={u.coverageAreas.map((a) => a.name).join(", ")}
            >
              {u.coverageAreas.map((a) => a.name).join(", ")}
            </span>
          ) : (
            <span className="text-slate-400">—</span>
          ),
      },
      {
        header: "Availability",
        accessor: "availability",
        className: COVERAGE_COL.availability,
        render: (u) => {
          const a = u.availability;
          if (!a?.hasAvailability) {
            return <span className="text-slate-400">—</span>;
          }
          return (
            <div className="flex items-start gap-2 min-w-0">
              <span
                className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${availabilityStatusDotClass(a)}`}
                aria-hidden
              />
              <div className="min-w-0">
                <p className="text-[13px] font-medium text-slate-800 truncate">
                  {a.primary}
                </p>
                {a.secondary ? (
                  <p className="text-[11px] text-slate-500 truncate">{a.secondary}</p>
                ) : null}
              </div>
            </div>
          );
        },
      },
    ];

    if (canEditListActions) {
      base.push({
        header: "Actions",
        id: "actions",
        className: COVERAGE_COL.actions,
        render: (u) => (
          <RowActions
            user={u}
            onEditCoverage={(user) => {
              closeActionMenus();
              setCoverageModalUser(user);
            }}
            onEditAvailability={(user) => {
              closeActionMenus();
              setAvailabilityModalUser(user);
            }}
          />
        ),
      });
    }

    return base;
  }, [canEditListActions, closeActionMenus]);

  const fetchCities = useCallback(async () => {
    try {
      const res = await request("/users/cities");
      if (res?.status === 1 && res?.result?.cities) {
        setCities(res.result.cities);
      }
    } catch (err) {
      console.error("Failed to load cities:", err);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("limit", String(limit));
      if (appliedSearch) params.set("search", appliedSearch);
      if (appliedCityIds.size > 0) {
        params.set("cityIds", Array.from(appliedCityIds).join(","));
      }
      if (appliedRoleCodes.size > 0) {
        params.set("role", Array.from(appliedRoleCodes).join(","));
      }
      if (appliedHasAvailability !== "all") {
        params.set("hasAvailability", appliedHasAvailability);
      }
      if (appliedHasCoverage !== "all") {
        params.set("hasCoverage", appliedHasCoverage);
      }
      if (appliedDayPresets.size > 0) {
        params.set("dayPresets", Array.from(appliedDayPresets).join(","));
      }
      if (appliedCustomDays.size > 0) {
        params.set("customDays", Array.from(appliedCustomDays).join(","));
      }
      if (appliedShiftDurations.size > 0) {
        params.set("shiftDurations", Array.from(appliedShiftDurations).join(","));
      }
      if (appliedShiftTypes.size > 0) {
        params.set("shiftTypes", Array.from(appliedShiftTypes).join(","));
      }

      const res = await request(`/users/coverage-areas?${params.toString()}`);
      if (res?.status === 1) {
        setUsers(res.result?.users || []);
        setTotal(res.result?.total ?? 0);
        setTotalPages(res.result?.totalPages ?? 1);
      } else {
        toast.error(res?.message || "Failed to load users");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to load users");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    limit,
    appliedSearch,
    appliedCityIds,
    appliedRoleCodes,
    appliedDayPresets,
    appliedCustomDays,
    appliedShiftDurations,
    appliedShiftTypes,
    appliedHasAvailability,
    appliedHasCoverage,
  ]);

  const filteredCities = cities.filter((c) =>
    c.name.toLowerCase().includes(citySearch.toLowerCase()),
  );

  const filteredRoles = roleOptions.filter(
    (r) =>
      r.code.toLowerCase().includes(roleSearch.toLowerCase()) ||
      r.name.toLowerCase().includes(roleSearch.toLowerCase()),
  );

  useEffect(() => {
    fetchCities();
  }, [fetchCities]);

  useEffect(() => {
    getRoles()
      .then((roles) =>
        setRoleOptions(
          roles
            .filter((r) => r.name !== "SRVY" && r.name !== "SuperAdmin")
            .map((r) => ({ id: r.id, name: r.name, code: r.name })),
        ),
      )
      .catch(() => setRoleOptions([]));
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handlePageChange = useCallback(
    (nextPage) => {
      closeActionMenus();
      setPage(nextPage);
    },
    [closeActionMenus],
  );

  const handleApply = () => {
    setAppliedSearch(search.trim());
    setAppliedCityIds(new Set(selectedCityIds));
    setAppliedRoleCodes(new Set(selectedRoleCodes));
    setAppliedDayPresets(new Set(selectedDayPresets));
    setAppliedCustomDays(new Set(selectedCustomDays));
    setAppliedShiftDurations(new Set(selectedShiftDurations));
    setAppliedShiftTypes(new Set(selectedShiftTypes));
    setAppliedHasAvailability(hasAvailability);
    setAppliedHasCoverage(hasCoverage);
    setPage(1);
  };

  const toggleInSet = (setter, value) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const clearFilters = () => {
    setSelectedCityIds(new Set());
    setSelectedRoleCodes(new Set());
    setSelectedDayPresets(new Set());
    setSelectedCustomDays(new Set());
    setSelectedShiftDurations(new Set());
    setSelectedShiftTypes(new Set());
    setHasAvailability("all");
    setHasCoverage("all");
    setSearch("");
    setAppliedSearch("");
    setAppliedCityIds(new Set());
    setAppliedRoleCodes(new Set());
    setAppliedDayPresets(new Set());
    setAppliedCustomDays(new Set());
    setAppliedShiftDurations(new Set());
    setAppliedShiftTypes(new Set());
    setAppliedHasAvailability("all");
    setAppliedHasCoverage("all");
    setCitySearch("");
    setRoleSearch("");
    setPage(1);
  };

  const draftFilterCount = countFilterState({
    search,
    cityIds: selectedCityIds,
    roleCodes: selectedRoleCodes,
    dayPresets: selectedDayPresets,
    customDays: selectedCustomDays,
    shiftDurations: selectedShiftDurations,
    shiftTypes: selectedShiftTypes,
    hasAvailability,
    hasCoverage,
  });

  const appliedFilterCount = countFilterState({
    search: appliedSearch,
    cityIds: appliedCityIds,
    roleCodes: appliedRoleCodes,
    dayPresets: appliedDayPresets,
    customDays: appliedCustomDays,
    shiftDurations: appliedShiftDurations,
    shiftTypes: appliedShiftTypes,
    hasAvailability: appliedHasAvailability,
    hasCoverage: appliedHasCoverage,
  });

  const showClearAll = appliedFilterCount > 0 || draftFilterCount > 0;

  const selectAllDays = () => {
    setSelectedDayPresets(new Set(DAY_PRESET_OPTIONS.map((o) => o.value)));
    setSelectedCustomDays(new Set(CUSTOM_DAY_OPTIONS.map((o) => o.value)));
  };

  const clearDays = () => {
    setSelectedDayPresets(new Set());
    setSelectedCustomDays(new Set());
  };

  const selectAllShifts = () => {
    setSelectedShiftDurations(new Set(SHIFT_DURATION_OPTIONS.map((o) => o.value)));
    setSelectedShiftTypes(new Set(SHIFT_TYPE_OPTIONS.map((o) => o.value)));
  };

  const clearShifts = () => {
    setSelectedShiftDurations(new Set());
    setSelectedShiftTypes(new Set());
  };

  return (
    <div className={PAGE_SHELL}>
      <div className={PAGE_HEADER_ROW}>
        <div>
          <h1 className={`text-2xl font-bold ${COVERAGE_THEME.section.title}`}>
            Coverage Area
          </h1>
          <p className={PAGE_SUBTITLE}>
            Users by coverage area, role and availability
          </p>
        </div>
        {showClearAll ? (
          <button type="button" onClick={clearFilters} className={COVERAGE_BTN.outline}>
            Clear All Filters
            {appliedFilterCount > 0 ? (
              <span
                className={`inline-flex items-center justify-center h-4 min-w-[1rem] px-1 rounded text-[10px] font-semibold tabular-nums ${COVERAGE_THEME.section.badge}`}
              >
                {appliedFilterCount}
              </span>
            ) : null}
          </button>
        ) : null}
      </div>

      <div
        className={`bg-white rounded-xl border shadow-sm overflow-hidden ${COVERAGE_THEME.section.wrap}`}
      >
        <div
          className={`flex items-center gap-2 h-11 px-4 border-b box-border ${COVERAGE_THEME.section.head}`}
        >
          <span
            className={`w-0.5 h-4 rounded-full shrink-0 ${COVERAGE_THEME.section.bar}`}
            aria-hidden
          />
          <h2
            className={`min-w-0 flex-1 text-xs font-semibold uppercase tracking-wide truncate ${COVERAGE_THEME.section.title}`}
          >
            Users
          </h2>
          <span
            className={`inline-flex items-center justify-center h-5 min-w-[1.25rem] px-1.5 rounded text-[10px] font-semibold tabular-nums shrink-0 ${COVERAGE_THEME.section.badge}`}
          >
            {total}
          </span>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-2 px-3 py-2 border-b border-indigo-100">
          <div className="relative flex-1 min-w-0">
            <SearchIcon
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleApply();
              }}
              className={COVERAGE_FIELD.search}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setFiltersOpen((open) => !open)}
              aria-expanded={filtersOpen}
              className={`${COVERAGE_BTN.outline} h-8 text-xs`}
            >
              Filters
              {draftFilterCount > 0 ? (
                <span
                  className={`inline-flex items-center justify-center h-4 min-w-[1rem] px-1 rounded text-[10px] font-semibold tabular-nums ${COVERAGE_THEME.section.badge}`}
                >
                  {draftFilterCount}
                </span>
              ) : null}
              <ChevronDownIcon
                size={14}
                className={`transition-transform ${filtersOpen ? "rotate-180" : ""}`}
              />
            </button>

            <button
              type="button"
              onClick={handleApply}
              className={`${COVERAGE_BTN.primary} h-8 text-xs`}
            >
              Apply
            </button>
          </div>
        </div>

        {filtersOpen ? (
          <div className="px-2 py-2 border-b border-indigo-100 bg-indigo-50/20">
            <div className="flex flex-wrap items-end gap-2 mb-2">
              <TriStateField
                label="Has Availability"
                value={hasAvailability}
                onChange={setHasAvailability}
                compact
              />
              <TriStateField
                label="Has Coverage Area"
                value={hasCoverage}
                onChange={setHasCoverage}
                compact
              />
              {appliedFilterCount > 0 ? (
                <span className="text-[10px] text-slate-500 ml-auto">
                  {appliedFilterCount} filter{appliedFilterCount === 1 ? "" : "s"} applied
                </span>
              ) : null}
            </div>

            <div className="overflow-x-auto pb-0.5">
              <div className="grid grid-cols-4 gap-2 min-w-[920px]">
                <CompactFilterColumn
                  title={`Cities (${cities.length})`}
                  onSelectAll={() =>
                    setSelectedCityIds(new Set(filteredCities.map((c) => c.id)))
                  }
                  onClear={() => setSelectedCityIds(new Set())}
                  searchValue={citySearch}
                  onSearchChange={setCitySearch}
                  searchPlaceholder="Search cities..."
                >
                  <CheckboxFilterList
                    items={filteredCities}
                    selected={selectedCityIds}
                    onToggle={(id) => toggleInSet(setSelectedCityIds, id)}
                    emptyMsg="No cities"
                  />
                </CompactFilterColumn>

                <CompactFilterColumn
                  title={`Roles (${roleOptions.length})`}
                  onSelectAll={() =>
                    setSelectedRoleCodes(new Set(filteredRoles.map((r) => r.code)))
                  }
                  onClear={() => setSelectedRoleCodes(new Set())}
                  searchValue={roleSearch}
                  onSearchChange={setRoleSearch}
                  searchPlaceholder="Search roles..."
                >
                  <CheckboxFilterList
                    items={filteredRoles}
                    selected={selectedRoleCodes}
                    onToggle={(code) => toggleInSet(setSelectedRoleCodes, code)}
                    emptyMsg="No roles"
                  />
                </CompactFilterColumn>

                <CompactFilterColumn
                  title="Availability — Days"
                  onSelectAll={selectAllDays}
                  onClear={clearDays}
                >
                  <div className={COVERAGE_CHECKBOX.listBoxCompact}>
                    {DAY_PRESET_OPTIONS.map((opt) => (
                      <CompactCheckboxRow
                        key={opt.value}
                        checked={selectedDayPresets.has(opt.value)}
                        onChange={() => toggleInSet(setSelectedDayPresets, opt.value)}
                        label={opt.label}
                      />
                    ))}
                  </div>
                  <div className={`${COVERAGE_CHECKBOX.listBoxCompact} mt-1 border-t border-indigo-50 pt-1`}>
                    {CUSTOM_DAY_OPTIONS.map((opt) => (
                      <CompactCheckboxRow
                        key={opt.value}
                        checked={selectedCustomDays.has(opt.value)}
                        onChange={() => toggleInSet(setSelectedCustomDays, opt.value)}
                        label={opt.label}
                      />
                    ))}
                  </div>
                </CompactFilterColumn>

                <CompactFilterColumn
                  title="Shifts"
                  onSelectAll={selectAllShifts}
                  onClear={clearShifts}
                >
                  <p className="text-[9px] font-semibold uppercase text-slate-500 px-0.5 mb-0.5">
                    Duration
                  </p>
                  <div className={COVERAGE_CHECKBOX.listBoxCompact}>
                    {SHIFT_DURATION_OPTIONS.map((opt) => (
                      <CompactCheckboxRow
                        key={opt.value}
                        checked={selectedShiftDurations.has(opt.value)}
                        onChange={() =>
                          toggleInSet(setSelectedShiftDurations, opt.value)
                        }
                        label={opt.label}
                      />
                    ))}
                  </div>
                  <p className="text-[9px] font-semibold uppercase text-slate-500 px-0.5 mt-1 mb-0.5">
                    Type
                  </p>
                  <div className={COVERAGE_CHECKBOX.listBoxCompact}>
                    {SHIFT_TYPE_OPTIONS.map((opt) => (
                      <CompactCheckboxRow
                        key={opt.value}
                        checked={selectedShiftTypes.has(opt.value)}
                        onChange={() => toggleInSet(setSelectedShiftTypes, opt.value)}
                        label={opt.label}
                        hint={opt.hint}
                      />
                    ))}
                  </div>
                </CompactFilterColumn>
              </div>
            </div>
          </div>
        ) : null}

        <Table
          columns={columns}
          data={users}
          keyField="id"
          loading={loading}
          density="compact"
          embedded
          tableClassName="table-fixed"
          srNoClassName={COVERAGE_COL.srNo}
          headTone={COVERAGE_THEME.tableHead}
          pagination={{
            currentPage: page,
            totalPages: totalPages,
            totalItems: total,
            itemsPerPage: limit,
            onPageChange: handlePageChange,
          }}
          noDataMessage="No users found."
        />
      </div>

      <CoverageAreaModal
        isOpen={Boolean(coverageModalUser)}
        onClose={() => setCoverageModalUser(null)}
        onSuccess={() => {
          setCoverageModalUser(null);
          fetchUsers();
        }}
        userId={coverageModalUser?.id}
        subjectName={formatUserName(coverageModalUser)}
      />

      <AvailabilityModal
        isOpen={Boolean(availabilityModalUser)}
        onClose={() => setAvailabilityModalUser(null)}
        onSuccess={() => {
          setAvailabilityModalUser(null);
          fetchUsers();
        }}
        userId={availabilityModalUser?.id}
        subjectName={formatUserName(availabilityModalUser)}
      />
    </div>
  );
};

const formatUserName = (user) =>
  [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim();

const RoleBadge = ({ name }) => {
  if (!name) return <span className="text-slate-400">—</span>;
  return (
    <span
      className={`inline-flex items-center h-5 px-1.5 rounded-md text-[10px] font-semibold uppercase tracking-wide border ${COVERAGE_ROLE_CHIP}`}
    >
      {name}
    </span>
  );
};

const TriStateField = ({ label, value, onChange, compact = false }) => (
  <div className={compact ? "min-w-[9.5rem]" : ""}>
    <label className={compact ? COVERAGE_FIELD.sectionLabelCompact : COVERAGE_FIELD.sectionLabel}>
      {label}
    </label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`${COVERAGE_FIELD.searchSm} w-full ${compact ? "h-7" : ""}`}
    >
      {TRI_STATE_OPTIONS.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </div>
);

const CompactFilterColumn = ({
  title,
  children,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  onSelectAll,
  onClear,
}) => (
  <div className={COVERAGE_CHECKBOX.filterColumn}>
    <div className={COVERAGE_CHECKBOX.filterColumnHead}>
      <p className={COVERAGE_FIELD.sectionLabelCompact}>{title}</p>
      <div className="flex items-center gap-1.5 shrink-0">
        <button type="button" className={COVERAGE_BTN.link} onClick={onSelectAll}>
          All
        </button>
        <button type="button" className={COVERAGE_BTN.link} onClick={onClear}>
          Clear
        </button>
      </div>
    </div>
    {searchPlaceholder ? (
      <div className="px-2 pt-1.5 shrink-0">
        <div className="relative">
          <SearchIcon
            size={12}
            className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
          />
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            className={`${COVERAGE_FIELD.searchSm} h-7 pl-7`}
          />
        </div>
      </div>
    ) : null}
    <div className={COVERAGE_CHECKBOX.filterColumnBody}>{children}</div>
  </div>
);

const CompactCheckboxRow = ({ checked, onChange, label, hint }) => (
  <label className="flex items-start gap-1.5 cursor-pointer rounded px-0.5 py-0.5 hover:bg-slate-50 min-w-0">
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className={`${COVERAGE_CHECKBOX.input} mt-0.5`}
    />
    <span className="min-w-0 leading-tight">
      <span className="block text-[11px] text-slate-800 truncate">{label}</span>
      {hint ? (
        <span className="block text-[9px] text-slate-500 truncate">{hint}</span>
      ) : null}
    </span>
  </label>
);

const CheckboxFilterList = ({ items, selected, onToggle, emptyMsg }) => (
  <div className={COVERAGE_CHECKBOX.listBoxCompact}>
    {items.length === 0 ? (
      <p className="col-span-full text-[10px] text-slate-500 py-1 px-1">{emptyMsg}</p>
    ) : (
      items.map((item) => {
        const val = item.code || item.id;
        const isSelected = selected.has(val);
        return (
          <label
            key={val}
            className={`flex items-center gap-1.5 cursor-pointer rounded px-0.5 py-0.5 transition-colors min-w-0 ${
              isSelected ? COVERAGE_CHECKBOX.wrapSelected : COVERAGE_CHECKBOX.wrapIdle
            }`}
          >
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onToggle(val)}
              className={COVERAGE_CHECKBOX.input}
            />
            <span
              className={
                isSelected ? COVERAGE_CHECKBOX.labelSelected : COVERAGE_CHECKBOX.labelIdle
              }
            >
              {item.name || item.code}
            </span>
          </label>
        );
      })
    )}
  </div>
);

const RowActions = ({ user, onEditCoverage, onEditAvailability }) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onCloseAll = () => setOpen(false);

    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    window.addEventListener("coverage-area-close-menus", onCloseAll);

    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
      window.removeEventListener("coverage-area-close-menus", onCloseAll);
    };
  }, [open]);

  if (!canManageRowCoverageActions(user)) {
    return <span className="text-slate-400 text-xs">—</span>;
  }

  const userLabel = formatUserName(user) || user.email || "User";

  return (
    <div className="relative inline-flex justify-end" ref={menuRef}>
      <button
        type="button"
        className={COVERAGE_BTN.edit}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={`Edit ${userLabel}`}
        title={`Edit ${userLabel}`}
        onClick={() => setOpen((prev) => !prev)}
      >
        <EditIcon size={13} />
        <span>Edit</span>
        <ChevronDownIcon
          size={12}
          className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open ? (
        <div className={COVERAGE_BTN.menuPanel} role="menu">
          <button
            type="button"
            role="menuitem"
            className={COVERAGE_BTN.menuItem}
            onClick={() => {
              setOpen(false);
              onEditCoverage(user);
            }}
          >
            <MapPinIcon size={14} className={COVERAGE_BTN.menuItemIcon} />
            Edit Coverage Area
          </button>
          <button
            type="button"
            role="menuitem"
            className={COVERAGE_BTN.menuItem}
            onClick={() => {
              setOpen(false);
              onEditAvailability(user);
            }}
          >
            <ClockIcon size={14} className={COVERAGE_BTN.menuItemIcon} />
            Edit Availability
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default CoverageArea;
